/**
 * Turns link values typed into i4/ILR2 data — anchor hrefs, homepage and email
 * fields, agent URLs — into hrefs that are safe to render. Only http(s) and
 * mailto hrefs are ever returned.
 */

const WEB_URL = /^https?:\/\/[^\s/?#]+\.[^\s/?#]/i;
const BARE_HOST = /^[a-z0-9][a-z0-9.-]*\.([a-z]{2,})([/:?#].*)?$/i;
/** An email address, unanchored — use `.source` to build search patterns. */
export const EMAIL_PATTERN =
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/;
const EMAIL_ADDRESS = new RegExp(`^(?:${EMAIL_PATTERN.source})$`);

// Relative i4 links look like hosts ("i4.dll?1=...", "flyer.pdf"); these
// "top-level domains" are file types, not sites.
const FILE_EXTENSIONS = new Set([
  'asp',
  'aspx',
  'bmp',
  'cgi',
  'css',
  'csv',
  'dll',
  'doc',
  'docx',
  'exe',
  'gif',
  'htm',
  'html',
  'jpeg',
  'jpg',
  'js',
  'pdf',
  'php',
  'png',
  'ppt',
  'pptx',
  'txt',
  'xls',
  'xlsx',
]);

const NEW_TAB = { target: '_blank', rel: 'noopener noreferrer' } as const;

/**
 * Normalize a candidate href/plain URL for `target=_blank` external links.
 * Rejects dangerous schemes (`javascript:`, `data:`, `vbscript:`), hosts
 * without a dot, and file names. Relative paths require `baseUrl`.
 */
export function normalizeExternalHref(
  href: string | null | undefined,
  baseUrl?: string | null,
): string | null {
  if (href == null) return null;
  const trimmed = String(href).trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:')
  ) {
    return null;
  }
  if (lower.startsWith('https://') || lower.startsWith('http://')) {
    return WEB_URL.test(trimmed) ? trimmed : null;
  }
  if (trimmed.startsWith('//')) {
    const absolute = `https:${trimmed}`;
    return WEB_URL.test(absolute) ? absolute : null;
  }

  if (trimmed.startsWith('/')) {
    const base = (baseUrl ?? '').trim();
    if (!base) return null;
    try {
      return new URL(trimmed, base.endsWith('/') ? base : `${base}/`).href;
    } catch {
      return null;
    }
  }

  // Bare host or host/path without scheme (i4 often prepends http://).
  const bareHost = BARE_HOST.exec(trimmed);
  if (bareHost?.[1] && !FILE_EXTENSIONS.has(bareHost[1].toLowerCase())) {
    return `http://${trimmed}`;
  }

  return null;
}

function mailtoHref(address: string, subject?: string) {
  return subject
    ? `mailto:${address}?subject=${encodeURIComponent(subject)}`
    : `mailto:${address}`;
}

/**
 * The http(s) or mailto href for a whole value that is a web address or an
 * email address, or `null` for anything else (phone numbers, names, relative
 * i4 paths). A bare email gets `emailSubject`; a mailto keeps its own query.
 */
export function toExternalHref(
  value: string | null | undefined,
  emailSubject?: string,
): string | null {
  const v = value?.trim() ?? '';
  if (/^mailto:/i.test(v)) {
    const address = v.slice('mailto:'.length).split('?')[0]?.trim() ?? '';
    return EMAIL_ADDRESS.test(address) ? v : null;
  }
  if (!v || /\s/.test(v)) return null;
  if (EMAIL_ADDRESS.test(v)) return mailtoHref(v, emailSubject);
  return normalizeExternalHref(v);
}

/** Web links open in a new tab without an opener; mail links open in place. */
export function externalLinkAttributes(href: string): {
  target?: string;
  rel?: string;
} {
  return /^mailto:/i.test(href) ? {} : NEW_TAB;
}
